import behaviourFileUploadsConstants from "@/constants/behaviourFileUploadsConstants.js"

const {
  UPLOAD_STATUS,
  UPLOAD_STATUS_ALIASES,
  UPLOAD_MODULE,
  UPLOAD_MODULE_ALIASES
} = behaviourFileUploadsConstants

/**
 * @param {unknown} status
 * @returns {string}
 */
export function normalizeBehaviourUploadStatus(status) {
  const raw = String(status ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")

  if (!raw) return ""

  for (const [normalized, aliases] of Object.entries(UPLOAD_STATUS_ALIASES)) {
    if (aliases.includes(raw)) return normalized
  }

  return raw
}

/**
 * @param {unknown} status
 * @returns {boolean}
 */
export function isBehaviourUploadFailedStatus(status) {
  return normalizeBehaviourUploadStatus(status) === UPLOAD_STATUS.FAILED
}

/**
 * @param {unknown} moduleValue
 * @returns {string}
 */
export function normalizeBehaviourUploadModule(moduleValue) {
  const raw = String(moduleValue ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_")

  if (!raw) return ""

  for (const [normalized, aliases] of Object.entries(UPLOAD_MODULE_ALIASES)) {
    if (aliases.includes(raw)) return normalized
  }

  return raw
}

/**
 * @param {unknown} moduleValue
 * @param {(key: string, values?: Record<string, string>) => string} t
 * @param {{ point?: string } | null | undefined} [terminology]
 * @returns {string}
 */
export function resolveBehaviourUploadModuleLabel(
  moduleValue,
  t,
  terminology = null
) {
  const normalized = normalizeBehaviourUploadModule(moduleValue)
  if (!normalized) return "—"

  if (normalized === UPLOAD_MODULE.AWARD) {
    return t("behaviour_manage.upload_module_award")
  }
  if (normalized === UPLOAD_MODULE.REDEEM) {
    return t("behaviour_manage.upload_module_redeem")
  }
  if (normalized === UPLOAD_MODULE.CORRECTION) {
    return t("behaviour_manage.upload_module_correction", {
      point: terminology?.point || "Point"
    })
  }

  return String(moduleValue ?? "").trim() || "—"
}

/**
 * @param {{
 *   page?: number,
 *   perPage?: number,
 *   search?: string,
 *   fromDate?: string,
 *   toDate?: string
 * }} args
 * @returns {Record<string, string | number>}
 */
export function buildBehaviourFileUploadsParams({
  page = 1,
  perPage = 25,
  search = "",
  fromDate = "",
  toDate = ""
} = {}) {
  const params = {
    page: Math.max(1, Number(page) || 1),
    per_page: Math.max(1, Number(perPage) || 25)
  }

  const q = String(search ?? "").trim()
  if (q) {
    params.search = q
  }

  const from = String(fromDate ?? "").trim()
  const to = String(toDate ?? "").trim()
  if (from) {
    params.from_date = from
  }
  if (to) {
    params.to_date = to
  }

  return params
}

/**
 * @param {Record<string, unknown>} item
 * @returns {Record<string, unknown>}
 */
export function mapApiBehaviourFileUploadRow(item) {
  const id = item?.id ?? item?.file_id ?? null
  const fileName = String(
    item?.file_name ?? item?.filename ?? item?.file ?? item?.name ?? ""
  ).trim()
  const moduleRaw =
    item?.module ??
    item?.module_name ??
    item?.upload_module ??
    item?.upload_type ??
    item?.type ??
    item?.transaction_type ??
    item?.activity_type ??
    ""
  const moduleKey = normalizeBehaviourUploadModule(moduleRaw)
  const dateTime =
    item?.date_time ??
    item?.created_at ??
    item?.uploaded_at ??
    item?.created ??
    item?.occurred_at ??
    ""
  const status = normalizeBehaviourUploadStatus(
    item?.status ?? item?.upload_status ?? ""
  )
  const errorLog =
    item?.error_log ??
    item?.error_logs ??
    item?.errors ??
    item?.error ??
    item?.failure_log ??
    null

  return {
    id,
    fileName: fileName || "—",
    module: moduleKey || String(moduleRaw ?? "").trim() || "",
    dateTime,
    status,
    errorLog,
    rawStatus: item?.status ?? item?.upload_status ?? ""
  }
}

/**
 * Normalize API pagination (Laravel meta or PBIS pagination) for
 * `buildPaginationFromApiResponse`, which expects `total_records` / `total_pages`.
 * @param {Record<string, unknown> | null | undefined} pagination
 * @param {number} [fallbackRowCount=0]
 * @returns {Record<string, number>}
 */
export function normalizeBehaviourFileUploadsPagination(
  pagination,
  fallbackRowCount = 0
) {
  const raw = pagination && typeof pagination === "object" ? pagination : {}
  const perPage =
    Number(raw.per_page ?? raw.perPage ?? raw.limit) ||
    Number(fallbackRowCount) ||
    25
  const total =
    Number(raw.total_records ?? raw.totalRecords ?? raw.total ?? raw.count) ||
    Number(fallbackRowCount) ||
    0
  const currentPage = Math.max(
    1,
    Number(raw.current_page ?? raw.currentPage ?? raw.page) || 1
  )
  const totalPages = Math.max(
    1,
    Number(
      raw.total_pages ?? raw.totalPages ?? raw.last_page ?? raw.lastPage
    ) || (perPage > 0 ? Math.ceil(total / perPage) || 1 : 1)
  )

  return {
    total_records: total,
    current_page: currentPage,
    total_pages: totalPages,
    per_page: perPage
  }
}

/**
 * @param {Record<string, unknown> | null | undefined} body
 * @returns {{ rows: Record<string, unknown>[], pagination: Record<string, unknown> }}
 */
export function parseBehaviourFileUploadsResponse(body) {
  const list = Array.isArray(body?.data)
    ? body.data
    : Array.isArray(body?.files)
      ? body.files
      : Array.isArray(body)
        ? body
        : []

  const rows = list.map(mapApiBehaviourFileUploadRow)
  const rawPagination = body?.pagination || body?.meta || {}

  return {
    rows,
    pagination: normalizeBehaviourFileUploadsPagination(
      rawPagination,
      rows.length
    )
  }
}

/**
 * Converts an error log JSON object (or string/array) into downloadable text.
 * @param {unknown} errorLog
 * @returns {string}
 */
export function formatBehaviourUploadErrorLogAsText(errorLog) {
  if (errorLog == null) return ""

  if (typeof errorLog === "string") {
    const trimmed = errorLog.trim()
    if (!trimmed) return ""
    try {
      const parsed = JSON.parse(trimmed)
      return formatBehaviourUploadErrorLogAsText(parsed)
    } catch {
      return trimmed
    }
  }

  if (Array.isArray(errorLog)) {
    return errorLog
      .map((entry) => {
        if (typeof entry === "string") return entry
        try {
          return JSON.stringify(entry, null, 2)
        } catch {
          return String(entry ?? "")
        }
      })
      .filter(Boolean)
      .join("\n")
  }

  if (typeof errorLog === "object") {
    try {
      return JSON.stringify(errorLog, null, 2)
    } catch {
      return String(errorLog)
    }
  }

  return String(errorLog)
}

/**
 * Builds a safe download basename from a CSV file name.
 * @param {string} fileName
 * @returns {string}
 */
export function buildBehaviourUploadErrorLogFileName(fileName) {
  const base = String(fileName ?? "")
    .trim()
    .replace(/\.[^.]+$/, "")
    .replace(/[^\w.-]+/g, "_")
    .replace(/^_+|_+$/g, "")

  return base ? `${base}-error-log` : "upload-error-log"
}
